<template>
  <div class="camera-scanner-backdrop" @click.self="$emit('close')">
    <div class="camera-scanner">
      <div class="scanner-header">
        <h3>{{ $t('products.scanBarcode') }}</h3>
        <button class="close-btn" @click="$emit('close')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div class="scanner-body">
        <!-- Camera container (always rendered for html5-qrcode) -->
        <div id="qr-reader" ref="scannerElement"></div>

        <!-- Loading overlay (on top of camera while initializing) -->
        <div v-if="isLoading && !error" class="scanner-loading-overlay">
          <div class="loading-spinner"></div>
          <p>Initializing camera...</p>
        </div>

        <div v-if="error" class="scanner-error">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p>{{ error }}</p>
          <button class="retry-btn" @click="startScanner">Retry</button>
        </div>

        <div v-if="lastScanned" class="last-scanned">
          Last scanned: <strong>{{ lastScanned }}</strong>
        </div>
      </div>

      <div class="scanner-footer">
        <p class="hint">Position the barcode within the frame</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { Html5Qrcode } from 'html5-qrcode'

const props = defineProps({
  onScan: {
    type: Function,
    required: true,
  },
})

const emit = defineEmits(['close', 'scan'])

const scannerElement = ref(null)
const error = ref(null)
const lastScanned = ref(null)
const isLoading = ref(true)

let html5QrCode = null
let isScanning = false

const startScanner = async () => {
  isLoading.value = true
  error.value = null

  try {
    html5QrCode = new Html5Qrcode('qr-reader')

    const config = {
      fps: 10,
      qrbox: { width: 250, height: 150 },
      aspectRatio: 1.5,
    }

    await html5QrCode.start(
      { facingMode: 'environment' }, // Rear camera
      config,
      (decodedText) => {
        // Avoid duplicate scans
        if (decodedText !== lastScanned.value) {
          lastScanned.value = decodedText
          props.onScan(decodedText)
          emit('scan', decodedText)
        }
      },
      () => {
        // QR code scan error (ignore - happens on every frame without barcode)
      }
    )
    isScanning = true
    isLoading.value = false
  } catch (err) {
    console.error('Camera scanner error:', err)
    isLoading.value = false
    const msg = err?.message || err?.name || String(err)
    if (err.name === 'NotAllowedError' || msg.includes('NotAllowedError') || msg.includes('Permission')) {
      error.value = 'Camera access was denied. Please allow camera access in your browser settings and try again.'
    } else if (err.name === 'NotFoundError' || msg.includes('NotFoundError') || msg.includes('device not found')) {
      error.value = 'No camera detected. Please connect a camera or use a device with a built-in camera.'
    } else if (err.name === 'NotReadableError' || msg.includes('NotReadableError') || msg.includes('Could not start')) {
      error.value = 'Camera is in use by another app. Please close other apps using the camera and try again.'
    } else if (err.name === 'OverconstrainedError' || msg.includes('OverconstrainedError')) {
      error.value = 'Your camera does not support the required settings. Try a different camera.'
    } else {
      error.value = 'Unable to open camera. Please check your camera connection and try again.'
    }
  }
}

const stopScanner = async () => {
  if (html5QrCode && isScanning) {
    try {
      await html5QrCode.stop()
      isScanning = false
    } catch (err) {
      console.warn('Error stopping scanner:', err)
    }
  }
}

onMounted(() => {
  startScanner()
})

onUnmounted(() => {
  stopScanner()
})

defineExpose({
  startScanner,
  stopScanner,
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.camera-scanner-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
}

.camera-scanner {
  background: var(--color-white);
  border-radius: var(--radius-xl, 16px);
  width: 100%;
  max-width: 450px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.scanner-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-gray-200);
}

.scanner-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--color-gray-100);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--color-gray-600);
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: var(--color-gray-200);
  color: var(--color-charcoal);
}

.scanner-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-5);
  position: relative;
  background: var(--color-gray-50);
  min-height: 280px;
}

/* Loading Overlay */
.scanner-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  background: var(--color-gray-50);
  z-index: 10;
}

.scanner-loading-overlay p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-gray-200);
  border-top-color: var(--color-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

#qr-reader {
  width: 100%;
  max-width: 350px;
  border-radius: var(--radius-lg);
  overflow: hidden;
}

:deep(#qr-reader video) {
  border-radius: var(--radius-lg);
}

:deep(#qr-reader__scan_region) {
  background: transparent !important;
}

:deep(#qr-reader__dashboard) {
  display: none !important;
}

.scanner-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-6);
  text-align: center;
  color: var(--color-gray-600);
}

.scanner-error svg {
  color: var(--color-red);
}

.scanner-error p {
  margin: 0;
  max-width: 280px;
  line-height: 1.5;
}

.retry-btn {
  padding: var(--space-2) var(--space-5);
  background: var(--color-blue);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.retry-btn:hover {
  background: var(--color-blue-dark, #0046cc);
}

.last-scanned {
  margin-top: var(--space-4);
  padding: var(--space-2) var(--space-4);
  background: var(--color-green-50, #f0fdf4);
  border: 1px solid var(--color-green-200, #bbf7d0);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-green-700, #15803d);
}

.scanner-footer {
  padding: var(--space-3) var(--space-5);
  text-align: center;
  border-top: 1px solid var(--color-gray-200);
  background: var(--color-white);
}

.hint {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}
</style>
